import React, { useState, useEffect } from 'react';
import { formatDate } from '../../utils/dateFormatter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Loader2, FileText, Building2, Calendar, DollarSign, MapPin, AlertCircle, MessageSquare, RefreshCw,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const CandidateApplications = () => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState(null);
  const [eligibility, setEligibility] = useState({});
  const [requestsByApplication, setRequestsByApplication] = useState({});

  // { applicationId, requestType } or null
  const [requestDialog, setRequestDialog] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('Not authenticated');
        return;
      }

      const { data: candidate } = await supabase
        .from('candidates')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!candidate) {
        setError('Candidate profile not found');
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('applications')
        .select(`
          *,
          rfq:rfqs(ref_code, title, client:clients(name))
        `)
        .eq('candidate_id', candidate.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setApplications(data || []);

      if (data && data.length > 0) {
        await Promise.all([
          fetchEligibility(data.map((a) => a.id)),
          fetchRequests(candidate.id),
        ]);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibility = async (applicationIds) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/update-requests/eligibility?application_ids=${applicationIds.join(',')}`,
        { headers: { 'Authorization': `Bearer ${session.access_token}` } }
      );
      const data = await response.json();
      if (response.ok) setEligibility(data.eligibility || {});
    } catch (err) {
      console.error('Error fetching request eligibility:', err);
    }
  };

  const fetchRequests = async (candidateId) => {
    try {
      const { data, error: requestsError } = await supabase
        .from('application_update_requests')
        .select('*')
        .eq('candidate_id', candidateId)
        .eq('status', 'responded')
        .order('responded_at', { ascending: false });

      if (requestsError) throw requestsError;

      const byApplication = {};
      (data || []).forEach((r) => {
        if (!byApplication[r.application_id]) byApplication[r.application_id] = [];
        byApplication[r.application_id].push(r);
      });
      setRequestsByApplication(byApplication);
    } catch (err) {
      console.error('Error fetching update request responses:', err);
    }
  };

  const openRequestDialog = (applicationId, requestType) => {
    setRequestMessage('');
    setSubmitError(null);
    setRequestDialog({ applicationId, requestType });
  };

  const submitRequest = async () => {
    if (!requestDialog) return;
    try {
      setSubmitting(true);
      setSubmitError(null);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/update-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          application_id: requestDialog.applicationId,
          request_type: requestDialog.requestType,
          message: requestMessage,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit request');
      }

      setRequestDialog(null);
      await fetchEligibility(applications.map((a) => a.id));
    } catch (err) {
      console.error('Error submitting request:', err);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
      case 'reviewing':
        return 'bg-blue-100 text-blue-800';
      case 'shortlisted':
        return 'bg-purple-100 text-purple-800';
      case 'interviewing':
        return 'bg-yellow-100 text-yellow-800';
      case 'offered':
        return 'bg-green-100 text-green-800';
      case 'accepted':
        return 'bg-green-600 text-white';
      case 'rejected':
      case 'unsuitable':
        return 'bg-red-100 text-red-800';
      case 'withdrawn':
        return 'bg-gray-100 text-gray-800';
      case 'pending_verification':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const statusUpdateHint = (elig) => {
    if (!elig) return null;
    if (elig.can_request) return null;
    if (elig.reason === 'cooldown_active' && elig.next_allowed_at) {
      return `You can request another update after ${formatDate(elig.next_allowed_at, { style: 'short' })}`;
    }
    if (elig.reason === 'already_requested') return 'You have already requested an update on this application';
    return null; // disabled_for_status - stay silent, no button, no explanation needed
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">My Applications</h1>
        <p className="text-muted-foreground mt-2">
          Track the status of your job applications
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {applications.length > 0 ? (
        <div className="space-y-4">
          {applications.map((app) => {
            const elig = eligibility[app.id];
            const isUnsuccessful = ['rejected', 'unsuitable'].includes(app.status);
            const hint = statusUpdateHint(elig?.status_update);
            const responses = requestsByApplication[app.id] || [];

            return (
              <Card key={app.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <CardTitle className="text-lg">{app.rfq?.title || 'Unknown Position'}</CardTitle>
                      <CardDescription className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        {app.rfq?.client?.name || 'Unknown Client'} • {app.rfq?.ref_code || 'N/A'}
                      </CardDescription>
                    </div>
                    <Badge className={getStatusColor(app.status)}>
                      {app.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>
                        Applied {formatDate(app.created_at, { style: 'short' })}
                      </span>
                    </div>
                    {app.asking_rate_value && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <DollarSign className="h-4 w-4" />
                        <span>${app.asking_rate_value}/{app.asking_rate_basis}</span>
                      </div>
                    )}
                    {app.locations_available && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span>{app.locations_available}</span>
                      </div>
                    )}
                  </div>

                  {app.status_changed_at && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Status updated {formatDate(app.status_changed_at, { style: 'short' })}
                    </p>
                  )}

                  {isUnsuccessful && app.client_feedback && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-start gap-2 text-sm bg-muted/50 rounded-md p-3">
                        <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                        <div>
                          <p className="font-medium mb-1">Feedback</p>
                          <p className="text-muted-foreground">{app.client_feedback}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {responses.map((r) => (
                    <div key={r.id} className="mt-4 pt-4 border-t">
                      <div className="flex items-start gap-2 text-sm bg-blue-50 rounded-md p-3">
                        <MessageSquare className="h-4 w-4 mt-0.5 shrink-0 text-blue-600" />
                        <div>
                          <p className="font-medium mb-1">
                            Response to your {r.request_type === 'feedback' ? 'feedback request' : 'update request'}
                          </p>
                          <p className="text-muted-foreground">{r.response_message}</p>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-3">
                    {elig?.status_update?.can_request && (
                      <Button size="sm" variant="outline" onClick={() => openRequestDialog(app.id, 'status_update')}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Request an update
                      </Button>
                    )}
                    {isUnsuccessful && !app.client_feedback && elig?.feedback?.can_request && (
                      <Button size="sm" variant="outline" onClick={() => openRequestDialog(app.id, 'feedback')}>
                        <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                        Request feedback
                      </Button>
                    )}
                    {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
                  </div>

                  {app.resume_url && (
                    <div className="mt-4 pt-4 border-t">
                      <a
                        href={app.resume_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-2"
                      >
                        <FileText className="h-4 w-4" />
                        View Submitted Resume
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Applications Yet</h3>
            <p className="text-muted-foreground mb-4">
              You haven't applied to any positions yet.
            </p>
            <a
              href="/candidate/job-board"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Browse Available Jobs
            </a>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!requestDialog} onOpenChange={(open) => !open && setRequestDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {requestDialog?.requestType === 'feedback' ? 'Request feedback' : 'Request an update'}
            </DialogTitle>
            <DialogDescription>
              {requestDialog?.requestType === 'feedback'
                ? "We'll ask the client for feedback on your behalf. There's no guarantee we'll receive it, but we'll let you know as soon as we hear anything."
                : "We'll let the recruiter know you'd like an update on this application."}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
            placeholder="Add an optional message..."
            rows={3}
          />

          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestDialog(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submitRequest} disabled={submitting}>
              {submitting ? 'Sending...' : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateApplications;
